import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BoxComponent from "../Components/Box";
import Sidebar from "../Components/Sidebar";
import Head from "../Components/Head";
import TypographyComponent from "../Components/Typography";
import ButtonComponent from "../Components/Button";
import DetailComponent from "../Components/DetailComponent";
import { getVehicleCategories } from "../api/constants";
import Cookies from "js-cookie";
import { toast } from "react-toastify";

export default function CategoryDetailsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [categoryData, setCategoryData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get category ID from URL parameters
  const queryParams = new URLSearchParams(location.search);
  const categoryId = queryParams.get("id");

  useEffect(() => {
    const fetchCategoryDetails = async () => {
      if (!categoryId) {
        toast.error("Category ID not provided");
        navigate("/vehicle-categories");
        return;
      }

      try {
        setLoading(true);
        const token = Cookies.get("token");

        // Fetch all categories and find the specific one
        const response = await getVehicleCategories(
          {
            limit: 1000, // Get all categories to find the specific one
            offset: 0,
            searchText: "",
          },
          token
        );

        if (response?.data?.data?.categories) {
          const category = response.data.data.categories.find(
            (cat) => cat._id === categoryId
          );

          if (category) {
            setCategoryData(category);
          } else {
            toast.error("Category not found");
            navigate("/vehicle-categories");
          }
        } else {
          toast.error("Failed to fetch category details");
          navigate("/vehicle-categories");
        }
      } catch (error) {
        console.error("Error fetching category details:", error);
        toast.error("Error loading category details");
        navigate("/vehicle-categories");
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryDetails();
  }, [categoryId, navigate]);

  const handleBack = () => {
    navigate("/vehicle-categories");
  };

  const handleEdit = () => {
    // Navigate back to categories page and trigger edit modal
    navigate("/vehicle-categories", {
      state: {
        editCategory: categoryData,
      },
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return "Invalid Date";
    }
  };

  if (loading) {
    return (
      <BoxComponent backgroundColor="var(--light)">
        <Head />
        <BoxComponent display="flex" justifyContent="space-between">
          <Sidebar />
          <BoxComponent
            display="flex"
            flexDirection="column"
            width="82%"
            padding="20px"
          >
            <BoxComponent
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="400px"
            >
              <TypographyComponent
                fontSize="18px"
                fontFamily="var(--main)"
                color="var(--dark)"
              >
                Loading category details...
              </TypographyComponent>
            </BoxComponent>
          </BoxComponent>
        </BoxComponent>
      </BoxComponent>
    );
  }

  if (!categoryData) {
    return (
      <BoxComponent backgroundColor="var(--light)">
        <Head />
        <BoxComponent display="flex" justifyContent="space-between">
          <Sidebar />
          <BoxComponent
            display="flex"
            flexDirection="column"
            width="82%"
            padding="20px"
          >
            <BoxComponent
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="400px"
            >
              <TypographyComponent
                fontSize="18px"
                fontFamily="var(--main)"
                color="var(--dark)"
              >
                Category not found
              </TypographyComponent>
            </BoxComponent>
          </BoxComponent>
        </BoxComponent>
      </BoxComponent>
    );
  }

  return (
    <BoxComponent backgroundColor="var(--light)">
      <Head />
      <BoxComponent display="flex" justifyContent="space-between">
        <Sidebar />
        <BoxComponent
          display="flex"
          flexDirection="column"
          width="82%"
          padding="20px"
        >
          {/* Header */}
          <BoxComponent
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            marginBottom="30px"
          >
            <BoxComponent display="flex" alignItems="center" gap="20px">
              <ButtonComponent
                variant="outlined"
                backgroundColor="transparent"
                sx={{
                  color: "var(--primary)",
                  borderColor: "var(--primary)",
                  padding: "8px 16px",
                }}
                onClick={handleBack}
              >
                ← Back to Categories
              </ButtonComponent>
              <TypographyComponent
                fontSize="24px"
                fontFamily="var(--main)"
                color="var(--dark)"
                fontWeight="600"
              >
                Category Details
              </TypographyComponent>
            </BoxComponent>
            <ButtonComponent
              variant="contained"
              backgroundColor="var(--primary)"
              sx={{ color: "var(--light)", padding: "10px 20px" }}
              onClick={handleEdit}
            >
              Edit Category
            </ButtonComponent>
          </BoxComponent>

          {/* Category Details Card */}
          <BoxComponent
            backgroundColor="var(--white)"
            borderRadius="12px"
            padding="30px"
            boxShadow="0 4px 12px rgba(0, 0, 0, 0.1)"
          >
            {/* Category Icon and Basic Info */}
            <BoxComponent
              display="flex"
              alignItems="flex-start"
              gap="30px"
              marginBottom="30px"
            >
              <BoxComponent
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap="15px"
              >
                <img
                  src={categoryData.iconURL}
                  alt={categoryData.name}
                  style={{
                    width: "120px",
                    height: "120px",
                    objectFit: "contain",
                    borderRadius: "12px",
                    border: "2px solid var(--light-gray)",
                  }}
                />
                <BoxComponent
                  backgroundColor={
                    categoryData.isActive ? "var(--success)" : "var(--error)"
                  }
                  color="white"
                  padding="6px 12px"
                  borderRadius="20px"
                  fontSize="12px"
                  fontWeight="600"
                >
                  {categoryData.isActive ? "ACTIVE" : "INACTIVE"}
                </BoxComponent>
              </BoxComponent>

              <BoxComponent flex="1">
                <TypographyComponent
                  fontSize="32px"
                  fontFamily="var(--main)"
                  color="var(--dark)"
                  fontWeight="700"
                  marginBottom="10px"
                >
                  {categoryData.name}
                </TypographyComponent>
                <TypographyComponent
                  fontSize="16px"
                  fontFamily="var(--main)"
                  color="var(--gray)"
                  marginBottom="20px"
                  lineHeight="1.6"
                >
                  {categoryData.description}
                </TypographyComponent>
              </BoxComponent>
            </BoxComponent>

            {/* Details Grid */}
            <BoxComponent
              display="grid"
              gridTemplateColumns="1fr 1fr"
              gap="20px"
              marginBottom="30px"
            >
              <DetailComponent title="Category ID" details={categoryData._id} />
              <DetailComponent
                title="Status"
                details={categoryData.isActive ? "Active" : "Inactive"}
              />
              <DetailComponent
                title="Minimum Seating Capacity"
                details={categoryData.minSeatingCapacity?.toString() || "N/A"}
              />
              <DetailComponent
                title="Maximum Seating Capacity"
                details={categoryData.maxSeatingCapacity?.toString() || "N/A"}
              />
              <DetailComponent
                title="Seating Capacity Range"
                details={`${categoryData.minSeatingCapacity || 0} - ${
                  categoryData.maxSeatingCapacity || 0
                } passengers`}
              />
              <DetailComponent
                title="Created Date"
                details={formatDateTime(categoryData.createdAt)}
              />
              <DetailComponent
                title="Last Updated"
                details={formatDateTime(categoryData.updatedAt)}
              />
            </BoxComponent>

            {/* Additional Information */}
            <BoxComponent
              borderTop="1px solid var(--light-gray)"
              paddingTop="20px"
            >
              <TypographyComponent
                fontSize="18px"
                fontFamily="var(--main)"
                color="var(--dark)"
                fontWeight="600"
                marginBottom="15px"
              >
                Additional Information
              </TypographyComponent>
              <BoxComponent
                display="grid"
                gridTemplateColumns="1fr 1fr"
                gap="20px"
              >
                <BoxComponent>
                  <TypographyComponent
                    fontSize="14px"
                    fontFamily="var(--main)"
                    color="var(--gray)"
                    marginBottom="5px"
                  >
                    Icon URL
                  </TypographyComponent>
                  <TypographyComponent
                    fontSize="14px"
                    fontFamily="var(--main)"
                    color="var(--dark)"
                    wordBreak="break-all"
                  >
                    {categoryData.iconURL}
                  </TypographyComponent>
                </BoxComponent>
              </BoxComponent>
            </BoxComponent>
          </BoxComponent>
        </BoxComponent>
      </BoxComponent>
    </BoxComponent>
  );
}
